<template>
  <div class="relative bg-white pt-16 pb-16">
    <div class="px-4 md:px-10 mx-auto w-full">
      <div class="flex flex-wrap mt-6">
        <div class="w-full px-4">
          <div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
            <div class="rounded-t mb-0 px-6 py-3 border-0">
              <div class="flex flex-wrap items-center">
                <div class="relative w-full px-4 max-w-full flex-grow flex-1">
                  <h3 class="font-semibold text-base text-blueGray-700">权限设置</h3>
                </div>
                <div class="px-4">
                  <el-button @click="$router.push('/admin/customers/users')">返回用户列表</el-button>
                </div>
              </div>
            </div>

            <div class="block w-full px-6 pb-6">
              <section class="authority-section">
                <div class="section-title">用户信息</div>
                <el-row :gutter="24" class="user-summary">
                  <el-col :span="16">
                    <div class="user-info">
                      <img v-if="userInfo.avatar" class="avatar" :src="userInfo.avatar" alt="" />
                      <div v-else class="avatar avatar-placeholder">{{ avatarText }}</div>
                      <div>
                        <p class="user-name">{{ userInfo.name || "-" }}</p>
                        <p class="user-id">id：{{ userInfo.user_id || uid }}</p>
                      </div>
                    </div>
                  </el-col>
                  <el-col :span="8" class="user-actions">
                    <el-tag :type="userInfo.is_vip ? 'warning' : 'info'" effect="plain">
                      {{ userInfo.is_vip ? "会员VIP" : "非会员" }}
                    </el-tag>
                    <el-button type="primary" @click="editAuthVisible = true">修改权限</el-button>
                    <el-button @click="editStaffVisible = true">员工管理</el-button>
                  </el-col>
                </el-row>

                <el-row :gutter="16" class="metrics-row">
                  <el-col v-for="item in userMetrics" :key="item.label" :xs="12" :sm="8" :md="4">
                    <div class="metric-item">
                      <p class="metric-label">{{ item.label }}</p>
                      <p class="metric-value">{{ item.value }}</p>
                    </div>
                  </el-col>
                </el-row>
              </section>

              <section class="authority-section">
                <div class="section-title">有效坐席列表</div>
                <div class="block w-full overflow-x-auto">
                  <el-table :data="staffSeatRecords" border stripe style="width: 100%; min-width: 760px;">
                    <el-table-column label="类型" width="120">
                      <template #default="scope">{{ scope.row.type == 1 ? "管理员" : "员工" }}</template>
                    </el-table-column>
                    <el-table-column prop="count" label="数量" width="100" />
                    <el-table-column label="添加时间">
                      <template #default="scope">{{ formatUnixTime(scope.row.ctime) }}</template>
                    </el-table-column>
                    <el-table-column label="到期时间">
                      <template #default="scope">{{ formatUnixTime(scope.row.expire) }}</template>
                    </el-table-column>
                    <el-table-column label="操作" width="120">
                      <template #default="scope">
                        <el-button type="primary" size="small" @click="showUpdateStaffDialog(scope.row)">更新</el-button>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </section>

              <section class="authority-section">
                <div class="section-title">订单信息</div>
                <el-input v-model="orderSearch" class="order-search" placeholder="请输入订单号" clearable>
                  <template #append>
                    <el-button @click="getDrawOrderList">
                      <i class="fas fa-search"></i>
                    </el-button>
                  </template>
                </el-input>
                <div class="block w-full overflow-x-auto">
                  <el-table v-loading="orderLoading" :data="orderData" border stripe style="width: 100%; min-width: 900px;">
                    <el-table-column prop="biz" label="产品" />
                    <el-table-column prop="out_trade_no" label="订单号" />
                    <el-table-column prop="fee" label="支付金额" />
                    <el-table-column prop="pay_way" label="支付方式" />
                    <el-table-column prop="pay_time" label="支付时间" />
                    <el-table-column prop="operator" label="操作人" />
                  </el-table>
                </div>
                <el-pagination
                  :current-page="orderPage"
                  :page-size="orderPageSize"
                  :page-sizes="[10, 50, 100]"
                  :total="orderTotal"
                  class="flex justify-end mt-4"
                  layout="total, sizes, prev, pager, next, jumper"
                  @size-change="orderPageSizeChange"
                  @current-change="orderPageChange"
                />
              </section>

              <section class="authority-section">
                <div class="section-title">发布/参与活动信息</div>
                <el-tabs v-model="tabValue" class="active-tabs" @tab-change="handleTab">
                  <el-tab-pane label="发布的活动" name="1">
                    <div class="block w-full overflow-x-auto">
                      <el-table v-loading="activityLoading" :data="publishData" border stripe style="width: 100%; min-width: 760px;">
                        <el-table-column prop="name" label="活动名称" />
                        <el-table-column prop="_id" label="活动id" />
                        <el-table-column label="创建时间">
                          <template #default="scope">{{ formatUnixTime(scope.row.ctime) }}</template>
                        </el-table-column>
                        <el-table-column prop="join_count" label="参与人数" width="120" />
                        <el-table-column label="操作" width="120">
                          <template #default="scope">
                            <el-button link type="primary" @click="handlePreview(scope.row._id)">查看活动</el-button>
                          </template>
                        </el-table-column>
                      </el-table>
                    </div>
                  </el-tab-pane>
                  <el-tab-pane label="参与的活动" name="2">
                    <div class="block w-full overflow-x-auto">
                      <el-table v-loading="activityLoading" :data="joinData" border stripe style="width: 100%; min-width: 760px;">
                        <el-table-column prop="name" label="活动名称" />
                        <el-table-column prop="task_id" label="活动id" />
                        <el-table-column label="参与时间">
                          <template #default="scope">{{ formatUnixTime(scope.row.join_time) }}</template>
                        </el-table-column>
                        <el-table-column label="操作" width="120">
                          <template #default="scope">
                            <el-button link type="primary" @click="handlePreview(scope.row.task_id)">查看活动</el-button>
                          </template>
                        </el-table-column>
                      </el-table>
                    </div>
                  </el-tab-pane>
                </el-tabs>
                <el-pagination
                  :current-page="page"
                  :page-size="pageSize"
                  :page-sizes="[10, 50, 100]"
                  :total="tabValue === '1' ? publishTotal : joinTotal"
                  class="flex justify-end mt-4"
                  layout="total, sizes, prev, pager, next, jumper"
                  @size-change="pageSizeChange"
                  @current-change="pageChange"
                />
              </section>

              <section class="authority-section">
                <div class="section-title">操作记录</div>
                <div class="block w-full overflow-x-auto">
                  <el-table v-loading="operLoading" :data="operData" border stripe style="width: 100%; min-width: 900px;">
                    <el-table-column label="操作类型" width="120">
                      <template #default="scope">
                        {{ scope.row.operate_type === "add" || scope.row.operate_type === "add_staff_count" ? "增加" : "减少" }}
                      </template>
                    </el-table-column>
                    <el-table-column label="操作前到期时间">
                      <template #default="scope">{{ formatUnixTime(scope.row.before) }}</template>
                    </el-table-column>
                    <el-table-column label="操作后到期时间">
                      <template #default="scope">{{ formatUnixTime(scope.row.after) }}</template>
                    </el-table-column>
                    <el-table-column label="操作信息">
                      <template #default="scope">{{ scope.row.msg || "-" }}</template>
                    </el-table-column>
                    <el-table-column label="操作时间">
                      <template #default="scope">{{ formatUnixTime(scope.row.ctime) }}</template>
                    </el-table-column>
                    <el-table-column prop="operate_by" label="操作人" />
                  </el-table>
                </div>
                <el-pagination
                  :current-page="operPage"
                  :page-size="operPageSize"
                  :page-sizes="[10, 50, 100]"
                  :total="operTotal"
                  class="flex justify-end mt-4"
                  layout="total, sizes, prev, pager, next, jumper"
                  @size-change="operPageSizeChange"
                  @current-change="operPageChange"
                />
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog v-model="activityVisible" title="查看活动" width="360px">
      <img class="activity-qrcode" :src="shareQrcodeUrl" alt="" />
      <template #footer>
        <el-button @click="activityVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="editAuthVisible" title="修改权限" width="420px" :close-on-click-modal="false">
      <el-form label-width="96px">
        <el-form-item label="操作">
          <el-radio-group v-model="editAuthForm.editType">
            <el-radio label="add">增加</el-radio>
            <el-radio label="sub" :disabled="!userInfo.is_vip">减少</el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="editAuthForm.editType === 'add'">
          <el-form-item label="权限类型">
            <el-select v-model="editAuthForm.bizType" placeholder="请选择">
              <el-option v-for="item in authBizOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="对应时长">
            <el-select v-model="editAuthForm.duration" placeholder="请选择">
              <el-option v-for="item in durationOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item v-if="editAuthForm.bizType === 3 || editAuthForm.bizType === 4" label="坐席数量">
            <el-input v-model="editAuthForm.count" type="number" />
          </el-form-item>
          <el-form-item label="订单号">
            <el-input v-model="editAuthForm.orderNo" />
          </el-form-item>
          <el-form-item label="订单金额">
            <el-input v-model="editAuthForm.amount" type="number" />
          </el-form-item>
        </template>
        <el-form-item v-else label="VIP有效期">
          <el-date-picker
            v-model="editAuthForm.reduceTime"
            type="datetime"
            placeholder="选择日期时间"
            :disabled-date="isReduceExpireDateDisabled"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editAuthVisible = false">取消</el-button>
        <el-button type="primary" :loading="submittingAuth" @click="submitEdit">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="editStaffVisible" title="员工管理" width="420px" :close-on-click-modal="false">
      <el-form label-width="96px">
        <el-form-item label="操作">
          <el-radio-group v-model="editStaffForm.editStaffType">
            <el-radio label="add_staff_count">增加</el-radio>
            <el-radio label="sub_staff_count">减少</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="员工类型">
          <el-select v-model="editStaffForm.staffType" placeholder="请选择">
            <el-option v-for="item in staffTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="数量">
          <el-input v-model="editStaffForm.staffCount" type="number" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editStaffVisible = false">取消</el-button>
        <el-button type="primary" :loading="submittingStaff" @click="submitEditStaff">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="updateStaffVisible" title="坐席管理" width="420px" :close-on-click-modal="false">
      <el-form label-width="96px">
        <el-form-item label="数量">
          <el-input v-model="updateStaffForm.count" type="number" />
        </el-form-item>
        <el-form-item label="到期时间">
          <el-date-picker v-model="updateStaffForm.expire" type="datetime" placeholder="选择日期时间" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="updateStaffVisible = false">取消</el-button>
        <el-button type="primary" :loading="updatingStaff" @click="doUpdateStaff">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { luckdrawApi } from "@/api/luckdraw.js";
import {
  AUTH_BIZ_OPTIONS,
  STAFF_TYPE_OPTIONS,
  buildEditAuthRequest,
  buildEditStaffRequest,
  buildUpdateStaffRequest,
  formatUnixTime,
  getDurationOptions,
  isReduceExpireDateDisabled,
  isPositiveInteger,
} from "./authorityPresenter.js";

function getDefaultEditAuthForm() {
  return {
    editType: "add",
    duration: 1,
    bizType: 1,
    orderNo: "",
    amount: null,
    count: 1,
    reduceTime: undefined,
  };
}

function getDefaultEditStaffForm() {
  return {
    editStaffType: "add_staff_count",
    staffType: "admin",
    staffCount: "",
  };
}

export default {
  name: "Authority",
  data() {
    return {
      uid: "",
      userInfo: {},
      tabValue: "1",
      page: 1,
      pageSize: 10,
      orderPage: 1,
      orderPageSize: 10,
      operPage: 1,
      operPageSize: 10,
      orderSearch: "",
      operData: [],
      operTotal: 0,
      orderData: [],
      orderTotal: 0,
      publishData: [],
      publishTotal: 0,
      joinData: [],
      joinTotal: 0,
      activityVisible: false,
      shareQrcodeUrl: "",
      editAuthVisible: false,
      editStaffVisible: false,
      updateStaffVisible: false,
      editAuthForm: getDefaultEditAuthForm(),
      editStaffForm: getDefaultEditStaffForm(),
      updateStaffForm: {
        count: 0,
        expire: undefined,
        id: "",
      },
      loading: false,
      orderLoading: false,
      activityLoading: false,
      operLoading: false,
      submittingAuth: false,
      submittingStaff: false,
      updatingStaff: false,
      authBizOptions: AUTH_BIZ_OPTIONS,
      staffTypeOptions: STAFF_TYPE_OPTIONS,
    };
  },
  computed: {
    avatarText() {
      return (this.userInfo.name || this.userInfo.user_id || "?").slice(0, 1);
    },
    staffSeatRecords() {
      return this.userInfo.staff_seat_records || [];
    },
    durationOptions() {
      return getDurationOptions(this.editAuthForm.bizType);
    },
    userMetrics() {
      return [
        { label: "最近使用时间", value: formatUnixTime(this.userInfo.last_login_time) },
        { label: "手机号", value: this.userInfo.phone || "-" },
        { label: "总购买次数", value: this.userInfo.total_pay_count || "-" },
        { label: "总购买金额", value: this.userInfo.total_pay_fee || "-" },
        { label: "发起活动数", value: this.userInfo.all_task_count || "-" },
        { label: "会员到期时间", value: formatUnixTime(this.userInfo.expire) },
        { label: "管理员数", value: `${this.userInfo.used_admin_count || 0}/${this.userInfo.max_admin_count || 0}` },
        { label: "普通员工数", value: `${this.userInfo.used_staff_count || 0}/${this.userInfo.max_staff_count || 0}` },
      ];
    },
  },
  watch: {
    "editAuthForm.bizType"(bizType) {
      const options = getDurationOptions(bizType);
      if (!options.some((item) => item.value === this.editAuthForm.duration)) {
        this.editAuthForm.duration = options[0]?.value || 1;
      }
    },
    "$route.params.uid": {
      immediate: true,
      handler(uid) {
        if (!uid) return;
        this.uid = uid;
        this.loadAllData();
      },
    },
  },
  methods: {
    formatUnixTime,
    isReduceExpireDateDisabled,
    async loadAllData() {
      await Promise.all([
        this.getUserInfo(),
        this.getDrawOrderList(),
        this.getActivityList(),
        this.getOperList(),
      ]);
    },
    async getUserInfo() {
      this.loading = true;
      try {
        const result = await luckdrawApi.getLottoUserInfo({ uid: this.uid });
        if (result.sta === 0) {
          this.userInfo = result.data || {};
        } else {
          throw new Error(result.msg || "获取用户信息失败");
        }
      } catch (error) {
        this.$message.error(error.message || "请求数据失败!");
      } finally {
        this.loading = false;
      }
    },
    async getOperList() {
      this.operLoading = true;
      try {
        const result = await luckdrawApi.operList({
          uid: this.uid,
          page: this.operPage,
          page_size: this.operPageSize,
        });
        if (result.sta === 0) {
          this.operData = result.data?.list || [];
          this.operTotal = result.data?.count || result.data?.total || 0;
        } else {
          throw new Error(result.msg || "获取操作记录失败");
        }
      } catch (error) {
        this.$message.error(error.message || "请求数据失败!");
      } finally {
        this.operLoading = false;
      }
    },
    async getDrawOrderList() {
      this.orderLoading = true;
      try {
        const result = await luckdrawApi.drawOrderList({
          uid: this.uid,
          page: this.orderPage,
          page_size: this.orderPageSize,
          order_no: this.orderSearch.replace(/\s/g, ""),
        });
        if (result.sta === 0) {
          this.orderData = result.data?.list || [];
          this.orderTotal = result.data?.count || result.data?.total || 0;
        } else {
          throw new Error(result.msg || "获取订单失败");
        }
      } catch (error) {
        this.$message.error(error.message || "请求数据失败!");
      } finally {
        this.orderLoading = false;
      }
    },
    async getActivityList() {
      this.activityLoading = true;
      try {
        const result = await luckdrawApi.drawActivityList({
          uid: this.uid,
          type: Number(this.tabValue),
          page: this.page,
          page_size: this.pageSize,
        });
        if (result.sta === 0) {
          const list = result.data?.list || [];
          const count = result.data?.count || result.data?.total || 0;
          if (this.tabValue === "1") {
            this.publishData = list;
            this.publishTotal = count;
          } else {
            this.joinData = list;
            this.joinTotal = count;
          }
        } else {
          throw new Error(result.msg || "获取活动失败");
        }
      } catch (error) {
        this.$message.error(error.message || "请求数据失败!");
      } finally {
        this.activityLoading = false;
      }
    },
    async handlePreview(id) {
      try {
        const result = await luckdrawApi.qrcode({ id });
        if (result.sta === 0) {
          this.shareQrcodeUrl = result.data?.promotion_wxa_qrcode_url || "";
          this.activityVisible = true;
        } else {
          throw new Error(result.msg || "获取二维码失败");
        }
      } catch (error) {
        this.$message.error(error.message || "请求数据失败!");
      }
    },
    handleTab() {
      this.page = 1;
      this.pageSize = 10;
      this.getActivityList();
    },
    operPageSizeChange(size) {
      this.operPageSize = size;
      this.operPage = 1;
      this.getOperList();
    },
    operPageChange(page) {
      this.operPage = page;
      this.getOperList();
    },
    orderPageSizeChange(size) {
      this.orderPageSize = size;
      this.orderPage = 1;
      this.getDrawOrderList();
    },
    orderPageChange(page) {
      this.orderPage = page;
      this.getDrawOrderList();
    },
    pageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.getActivityList();
    },
    pageChange(page) {
      this.page = page;
      this.getActivityList();
    },
    showUpdateStaffDialog(row) {
      this.updateStaffForm = {
        count: row.count,
        expire: row.expire ? new Date(row.expire * 1000) : undefined,
        id: row._id,
      };
      this.updateStaffVisible = true;
    },
    async doUpdateStaff() {
      if (!this.updateStaffForm.expire) {
        this.$message.error("请选择到期时间");
        return;
      }

      this.updatingStaff = true;
      try {
        const result = await luckdrawApi.updateStaff(buildUpdateStaffRequest(this.updateStaffForm));
        if (result.sta === 0) {
          this.updateStaffVisible = false;
          this.$message.success("更新成功");
          this.getUserInfo();
        } else {
          throw new Error(result.msg || "更新失败");
        }
      } catch (error) {
        this.$message.error(error.message || "请求数据失败!");
      } finally {
        this.updatingStaff = false;
      }
    },
    async submitEdit() {
      if (this.editAuthForm.editType === "add" && (this.editAuthForm.bizType === 3 || this.editAuthForm.bizType === 4) && !isPositiveInteger(this.editAuthForm.count)) {
        this.$message.error("请输入正整数");
        return;
      }

      if (this.editAuthForm.editType === "sub" && !this.editAuthForm.reduceTime) {
        this.$message.error("请选择VIP有效期");
        return;
      }

      this.submittingAuth = true;
      try {
        const result = await luckdrawApi.editOrder(buildEditAuthRequest(this.uid, this.editAuthForm));
        if (result.sta === 0) {
          this.editAuthVisible = false;
          this.editAuthForm = getDefaultEditAuthForm();
          this.$message.success("修改权限成功");
          this.getUserInfo();
          this.getDrawOrderList();
          this.getOperList();
        } else {
          throw new Error(result.msg || "修改权限失败");
        }
      } catch (error) {
        this.$message.error(error.message || "请求数据失败!");
      } finally {
        this.submittingAuth = false;
      }
    },
    async submitEditStaff() {
      if (!isPositiveInteger(this.editStaffForm.staffCount)) {
        this.$message.error("请输入正整数");
        return;
      }

      this.submittingStaff = true;
      try {
        const result = await luckdrawApi.editOrder(buildEditStaffRequest(this.uid, this.editStaffForm));
        if (result.sta === 0) {
          this.editStaffVisible = false;
          this.editStaffForm = getDefaultEditStaffForm();
          this.$message.success("修改员工成功");
          this.getUserInfo();
          this.getDrawOrderList();
          this.getOperList();
        } else {
          throw new Error(result.msg || "修改员工失败");
        }
      } catch (error) {
        this.$message.error(error.message || "请求数据失败!");
      } finally {
        this.submittingStaff = false;
      }
    },
  },
};
</script>

<style scoped>
.authority-section {
  padding: 20px 0 28px;
  border-top: 1px solid #e5e7eb;
}

.section-title {
  margin-bottom: 16px;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}

.user-summary {
  align-items: center;
  margin-bottom: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e5e7eb;
  color: #4b5563;
  font-weight: 600;
}

.user-name {
  margin: 0;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
}

.user-id {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 22px;
}

.user-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.metrics-row {
  row-gap: 16px;
}

.metric-item {
  min-height: 72px;
}

.metric-label {
  margin: 0 0 4px;
  color: #9ca3af;
  font-size: 12px;
  line-height: 20px;
}

.metric-value {
  margin: 0;
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  word-break: break-all;
}

.order-search {
  width: 320px;
  max-width: 100%;
  margin-bottom: 16px;
}

.active-tabs {
  margin-top: 8px;
}

.activity-qrcode {
  display: block;
  width: 160px;
  height: 160px;
  margin: auto;
}
</style>
